// Plantillas de agentes: punto de partida que el usuario adapta antes de guardar
// (nada se crea solo). Las usa la app de Agentes en el primer paso de «Crear agente».

import {
  CalendarClock,
  Calculator,
  Headset,
  LifeBuoy,
  PenLine,
  UserCheck,
} from 'lucide-react';

export type Tone = 'violet' | 'indigo' | 'blue' | 'magenta' | 'purple';

export interface AgentTemplate {
  key: string;
  title: string;
  summary: string;
  icon: JSX.Element;
  tone: Tone;
  name: string;
  emoji: string;
  instructions: string;
}

// Textos de partida: el usuario los adapta a su negocio antes de guardar.
export const TEMPLATES: AgentTemplate[] = [
  {
    key: 'atencion',
    title: 'Atención a clientes',
    summary: 'Responde dudas frecuentes con la información de tu negocio.',
    icon: <Headset size={18} />,
    tone: 'violet',
    name: 'Atención a clientes',
    emoji: '💬',
    instructions:
      'Eres el agente de atención a clientes de nuestra organización. Respondes con amabilidad y en pocas palabras.\n' +
      '- Usa solo la información de la organización; si no sabes algo, dilo y ofrece que una persona le dé seguimiento.\n' +
      '- Pregunta el nombre y cómo contactar a la persona cuando quiera seguimiento.\n' +
      '- No prometas precios, tiempos ni descuentos que no estén confirmados.',
  },
  {
    key: 'seguimiento',
    title: 'Seguimiento de prospectos',
    summary: 'Retoma conversaciones y ayuda a que el prospecto decida.',
    icon: <UserCheck size={18} />,
    tone: 'indigo',
    name: 'Seguimiento de prospectos',
    emoji: '🎯',
    instructions:
      'Ayudas al equipo a dar seguimiento a prospectos.\n' +
      '- Resume en qué quedó cada conversación y cuál es el siguiente paso.\n' +
      '- Propón mensajes de seguimiento breves y respetuosos; no insistas más de lo razonable.\n' +
      '- Si el prospecto pide no ser contactado, respétalo y avísalo al equipo.',
  },
  {
    key: 'cotizador',
    title: 'Cotizador',
    summary: 'Arma cotizaciones con tus productos y precios vigentes.',
    icon: <Calculator size={18} />,
    tone: 'blue',
    name: 'Cotizador',
    emoji: '🧾',
    instructions:
      'Preparas cotizaciones con los productos y precios vigentes de la organización.\n' +
      '- Pregunta lo necesario antes de cotizar (producto, cantidad, fecha, lugar).\n' +
      '- Muestra cada concepto por separado; no sumes totales tú: indica que el total lo confirma el equipo.\n' +
      '- Si falta un precio, dilo en lugar de inventarlo.',
  },
  {
    key: 'agenda',
    title: 'Agenda de citas',
    summary: 'Propone horarios y confirma citas con tus clientes.',
    icon: <CalendarClock size={18} />,
    tone: 'magenta',
    name: 'Agenda de citas',
    emoji: '📅',
    instructions:
      'Ayudas a agendar citas.\n' +
      '- Pregunta el motivo, el día y la hora que prefiere la persona.\n' +
      '- Confirma los datos antes de cerrar y recuerda el lugar o la liga de la cita.\n' +
      '- Si no hay horario disponible, ofrece alternativas.',
  },
  {
    key: 'contenido',
    title: 'Asistente de contenido',
    summary: 'Propone publicaciones con el tono de tu marca.',
    icon: <PenLine size={18} />,
    tone: 'purple',
    name: 'Asistente de contenido',
    emoji: '✍️',
    instructions:
      'Propones publicaciones para redes sociales con el tono de la organización.\n' +
      '- Escribe textos cortos y claros, en español de México.\n' +
      '- Ofrece 2 o 3 opciones y sugiere una imagen para cada una.\n' +
      '- Todo es borrador: una persona lo revisa antes de publicar.',
  },
  {
    key: 'soporte',
    title: 'Soporte interno',
    summary: 'Responde preguntas del equipo sobre procesos y políticas.',
    icon: <LifeBuoy size={18} />,
    tone: 'indigo',
    name: 'Soporte interno',
    emoji: '🛟',
    instructions:
      'Respondes preguntas del equipo sobre procesos, políticas y herramientas internas.\n' +
      '- Contesta con pasos concretos.\n' +
      '- Si algo no está documentado, dilo y sugiere a quién preguntar.\n' +
      '- No compartas datos personales de clientes ni de colegas.',
  },
];

